/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ItemCard } from '../components/ItemCard';
import { useTheme } from '../theme';
import { itemService } from '../services/itemService';

const Search: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get('q') || '';
  
  const [items, setItems] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    itemService.searchItems(searchQuery)
      .then(res => {
        const data = res.data;
        if (data.data) {
          setItems((data.data.items || []).map((item: any) => ({
            ...item,
            image: item.imageUrl,
            storeId: item.storeId
          })));
          setStores(data.data.stores || []);
        } else {
            setItems([]);
            setStores([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error searching items:', err);
        setLoading(false);
      });
  }, [searchQuery]);

  return (
    <div className="py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: theme.colors.text }}>
          {searchQuery ? `Search results for "${searchQuery}"` : 'All Items'}
        </h1>
        <p className="opacity-60" style={{ color: theme.colors.text }}>
          Found {items.length} items {stores.length > 0 && `and ${stores.length} shops`}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20">Loading results...</div>
      ) : (
        <>
          {stores.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6" style={{ color: theme.colors.text }}>Shops</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stores.map((store: any) => (
                  <Link 
                    key={store.id}
                    to={`/store/${store.userId}`}
                    className="flex items-center p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    style={{ backgroundColor: theme.colors.background }}
                  >
                    <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border" style={{ borderColor: theme.colors.secondary }}>
                      <img 
                        src={store.logoUrl || 'https://via.placeholder.com/150?text=Shop'} 
                        alt={store.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: theme.colors.text }}>{store.name}</h3>
                      <p className="text-sm line-clamp-1" style={{ color: theme.colors.text }}>{store.location}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {items.length > 0 ? (
            <div>
              {stores.length > 0 && (
                <h2 className="text-2xl font-bold mb-6" style={{ color: theme.colors.text }}>Items</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {items.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          ) : (
            stores.length === 0 && (
              <div className="text-center py-20 bg-gray-50 rounded-3xl border border-dashed" style={{ borderColor: theme.colors.border }}>
                <p className="text-xl font-medium mb-2">No results found</p>
                <p className="opacity-60">Try adjusting your search or filters</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default Search;
